/**
 * A flow map card for a map somebody has already loaded (`useFlowMapDoc`).
 *
 * The universal <FlowMap> from the widget package does the drawing; this card
 * wires it to the CRM: the CRM's language for its words, the save status at the
 * end of its toolbar, the conflict banner, the history, and in-app routing for
 * links that point at other CRM screens.
 *
 * `FlowMapPanel` is this card with the loading built in, which is what a guide
 * and a project want. A page that has to read the document itself while it is
 * being drawn (an AI call campaign checks every change) loads the map with the
 * hook and hands it here.
 *
 * Text is written as plain strings (no `textLocale`): a CRM map is written in
 * one language by the team that reads it, unlike a public page.
 *
 * Two host decisions are handed in rather than made here:
 * - `emptyState`: what an untouched empty map shows instead of the canvas. The
 *   guide draws a one-line invitation for editors and nothing for readers; the
 *   project tab offers to start from its milestones. It receives `start`, which
 *   saves a first document straight away.
 * - `quiet`: draw nothing while loading or after a failed load, for a page where
 *   the map is an extra rather than the subject (a guide is read for its text).
 *
 * Loading and failure look like the other project tabs (`MilestoneBoard.tsx`):
 * a spinner card, then an empty state that names the failure and offers a retry.
 */
import { useCallback, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { History, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { FlowMap } from "@wa/widgets/flow-map";
import { isSafeHref, type FlowLink, type FlowMapDoc, type FlowNodeMark, type FlowRefChoices } from "@wa/widgets/flow-map/model";
import { useI18n } from "../../../../i18n/I18nProvider";
import type { FlowOwner } from "../../../../lib/flowMap";
import { Btn, Card, CardHead, EmptyState } from "../../../ui";
import { FlowRevisionsModal } from "./FlowRevisionsModal";
import { ConflictBanner, SaveStatus } from "./SaveStatus";
import type { FlowMapDocApi } from "./useFlowMapDoc";

export interface FlowMapCardProps {
  owner: FlowOwner;
  ownerId: string;
  /** The map, loaded and kept saved by `useFlowMapDoc(owner, ownerId)`. */
  map: FlowMapDocApi;
  /** The page's own permission check. The server's `canEdit` has to agree as well. */
  canEdit: boolean;
  /** Canvas height: px, or any CSS length. */
  height?: number | string;
  /** The mouse wheel zooms the map. Leave unset for the map's default (on while editing). */
  scrollZoom?: boolean;
  /** Card heading, with the step count under it. Leave out where the page already names the map. */
  title?: ReactNode;
  /** Accessible name of the map. Defaults to the word "Flow map". */
  ariaLabel?: string;
  /**
   * Drawn instead of the canvas while the map is empty and untouched in this
   * visit. `start(doc)` puts a first document on it and saves it. Return null
   * to draw nothing at all. Without this prop an empty map shows its canvas.
   */
  emptyState?: (start: (doc: FlowMapDoc) => void) => ReactNode;
  /** Draw nothing while loading or after a failed load. */
  quiet?: boolean;
  /**
   * The host already knows this map exists (the guide's `hasFlowMap`). Its
   * space is then held open while it loads instead of collapsing, so the text
   * under it does not jump down the moment the map arrives. A quiet panel
   * without this draws nothing at all until the map is there.
   */
  expected?: boolean;
  /** Marks by node id, drawn on the canvas (an AI campaign marks the steps it cannot follow). */
  nodeMarks?: Readonly<Record<string, FlowNodeMark>>;
  /** A pointer the node panel offers as a choice (an AI campaign's end nodes pick the call's outcome). */
  refChoices?: FlowRefChoices;
  /** Drawn inside the card under the canvas: what the page has to say about the map. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The padding around the canvas, shared by the real map and the placeholder
 * that holds its space. One function so the two cannot drift apart.
 */
function bodyPad(hasTitle: boolean): string {
  return cn("px-4 pb-4 sm:px-[26px] sm:pb-[26px]", hasTitle ? "pt-3" : "pt-4 sm:pt-[26px]");
}

export function FlowMapCard({
  owner,
  ownerId,
  map,
  canEdit,
  height = 560,
  scrollZoom,
  title,
  ariaLabel,
  emptyState,
  quiet = false,
  expected = false,
  nodeMarks,
  refChoices,
  footer,
  className,
}: FlowMapCardProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [history, setHistory] = useState(false);

  // An app path is a screen of this CRM: routed in place rather than reloading
  // the whole app. Everything else (#anchor, ?query, http, mailto, tel) is left
  // to the map, which opens it the ordinary way.
  const openLink = useCallback(
    (link: FlowLink): boolean => {
      const href = link.href;
      if (href && href.startsWith("/") && isSafeHref(href)) {
        navigate(href);
        return true;
      }
      return false;
    },
    [navigate],
  );

  if (map.phase === "loading") {
    // A map the host has promised keeps its place: the same card, head and
    // canvas box, with a spinner where the steps will be.
    if (expected) {
      return (
        <Card className={className}>
          {title ? <CardHead title={title} sub={t("flowMap.loading")} /> : null}
          <div className={bodyPad(Boolean(title))}>
            <div
              className="flex items-center justify-center text-muted-foreground"
              style={{ height: typeof height === "number" ? `${height}px` : height }}
            >
              <Loader2 className="size-6 animate-spin" />
            </div>
          </div>
        </Card>
      );
    }
    return quiet ? null : (
      <div className={cn("crm-card flex justify-center py-16 text-muted-foreground", className)}>
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (map.phase !== "ready") {
    if (quiet) return null;
    return (
      <Card className={className}>
        <EmptyState
          icon={TriangleAlert}
          title={t("flowMap.loadFailed")}
          action={
            map.phase === "error" ? (
              <Btn small onClick={map.reload}>
                {t("flowMap.retry")}
              </Btn>
            ) : undefined
          }
        />
      </Card>
    );
  }

  // Empty AND untouched. Once the user has drawn anything in this visit the
  // canvas stays, even if they delete every node again: swapping it for the
  // invitation mid-edit would throw their undo history away under their hands.
  if (emptyState && map.doc.nodes.length === 0 && map.save.rev === 0) {
    return <>{emptyState(map.start)}</>;
  }

  // Both have to agree: the page's check keeps the tools from somebody who may
  // not use them, and the server's covers a rule the page did not foresee.
  const editable = canEdit && (map.view?.canEdit ?? false);
  const steps = map.doc.nodes.filter((n) => n.kind !== "group").length;
  // Version 1 is the first save, so there is a past to read only from 2 up.
  // Reading it needs no edit right; the restore button inside does.
  const version = map.view?.version ?? 0;
  const hasHistory = version > 1;
  // Undefined rather than an empty fragment: the toolbar draws a group around
  // whatever it is given, and an empty group is a stray divider.
  const toolbarExtra =
    hasHistory || editable ? (
      <>
        {hasHistory ? (
          <Btn small onClick={() => setHistory(true)}>
            <History className="size-3.5" />
            {t("flowMap.hist.open")}
          </Btn>
        ) : null}
        {editable ? <SaveStatus state={map.save} onRetry={map.retry} /> : null}
      </>
    ) : undefined;

  return (
    <Card className={className}>
      {title ? <CardHead title={title} sub={t("flowMap.subtitle", { n: steps })} /> : null}
      {map.save.conflict ? (
        <ConflictBanner who={map.save.conflict.updatedByName} onLoadLatest={map.loadLatest} onKeepMine={map.keepMine} />
      ) : null}
      <div className={bodyPad(Boolean(title))}>
        <FlowMap
          value={map.doc}
          onChange={editable ? map.edit : undefined}
          locale={locale}
          height={height}
          scrollZoom={scrollZoom}
          ariaLabel={ariaLabel ?? t("flowMap.title")}
          toolbarExtra={toolbarExtra}
          onOpenLink={openLink}
          nodeMarks={nodeMarks}
          refChoices={refChoices}
        />
        {footer ? <div className="mt-4">{footer}</div> : null}
      </div>
      {history ? (
        <FlowRevisionsModal
          owner={owner}
          ownerId={ownerId}
          canEdit={editable}
          currentVersion={version}
          // A restore is an ordinary save of a whole document: it writes the
          // map on screen into the history first and meets the same version
          // check as any other save.
          onRestore={map.start}
          onClose={() => setHistory(false)}
        />
      ) : null}
    </Card>
  );
}
